import styled, { keyframes, createGlobalStyle } from "styled-components";
import React, { useState } from 'react';
import { useStripe, useElements, Elements } from '@stripe/react-stripe-js';
import { IssuingCardNumberElement } from '@stripe/react-stripe-js';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { useAuth } from '../components/Auth/AuthContext';
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import InfoModal from '../components/VirtualCard/InfoModal';

const stripePromise = loadStripe('pk_test_51REnh3QsfTgT2h8hocOCpG8URjhFIF4ts8i2lP5c2PisnB9oJNH23xA8KhULzdvtJoWADBCU6lDm5gLGuk1RqonF00b3DMGUNa');

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: 'Very Sans Light';
    src: url('/assets/fonts/VerySans-LightText.ttf') format('truetype');
    font-weight: 300;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Medium';
    src: url('/assets/fonts/VerySans-MediumText.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Bold';
    src: url('/assets/fonts/VerySans-BoldText.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Very Sans Headline';
    src: url('/assets/fonts/VerySans-Headline.ttf') format('truetype');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Very Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    line-height: 1.6;
    color: #ffffff;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    overflow-x: hidden;
  }
`;

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  
  @media (max-width: 968px) {
    padding: 6rem 1rem;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 1100px;
  background: transparent;

  @media screen and (max-width: 768px) {
  width: 95%;}
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  h1 {
    font-size: 2rem;
    font-weight: 600;
    color: #ffffff;
    font-family: 'Very Sans Bold', sans-serif;
    margin-bottom: 0.5rem;
  }
`;

const FormCard = styled.form`
  width: 100%;
`;

const FormHeader = styled.div`
  text-align: left;
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  margin-bottom: 0.5rem;
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
`;

const FormSubtitle = styled.p`
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.95rem;
  margin: 0;
`;

const FormSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FormColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.9rem;
  font-weight: 400;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.95rem;
  font-family: 'Very Sans Medium', sans-serif;
  color: #ffffff;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: #94A3B8;
  }
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.95rem;
  font-family: 'Very Sans Medium', sans-serif;
  color: #ffffff;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    background: rgba(255, 255, 255, 0.08);
  }
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  option {
    background: #050B15;
    color: #ffffff;
  }
`;

const PhoneInputWrapper = styled.div`
  .PhoneInput {
    padding: 0.875rem 1rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.2s ease;
    
    &:focus-within {
      border-color: #3B82F6;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      background: rgba(255, 255, 255, 0.08);
    }
    
    &:hover {
      border-color: rgba(255, 255, 255, 0.2);
    }
  }
  
  .PhoneInputInput {
    background: transparent;
    border: none;
    outline: none;
    font-family: 'Very Sans Medium', sans-serif;
    font-size: 0.95rem;
    color: #ffffff;
    
    &::placeholder {
      color: #94A3B8;
    }
  }
  
  .PhoneInputCountryIcon {
    box-shadow: none;
    border-radius: 4px;
  }
  
  .PhoneInputCountrySelectArrow {
    color: #94A3B8;
  }
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  font-family: 'Very Sans Bold', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background: #3B82F6;
    color: white;
    
    &:hover {
      background: #2563EB;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
    
    &:disabled {
      background: rgba(255, 255, 255, 0.1);
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background: transparent;
    color: #94A3B8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    
    &:hover {
      background: rgba(59, 130, 246, 0.1);
      border-color: rgba(59, 130, 246, 0.3);
      color: #ffffff;
    }
  }
`;

const LoadingModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingContent = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2.5rem;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  max-width: 400px;
  width: 90%;
`;

const LoadingSpinner = styled.div`
  margin: 0 auto 1.5rem auto;
  display: flex;
  justify-content: center;
`;

const LoadingText = styled.p`
  color: #ffffff;
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 1rem;
  margin: 0;
`;

const Footer = styled.div`
  margin-top: 2rem;
  text-align: center;
  color: #94A3B8;
  font-size: 0.8rem;
  font-family: 'Very Sans Light', sans-serif;
`;

const VirtualCard = () => {
    const [firstName, setFirstName] = useState('');
    const [surname, setSurname] = useState('');
    const [address, setAddress] = useState('');
    const [email, setEmail] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [country, setCountry] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [stripeUserId, setStripeUserId] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [cardId, setCardId] = useState('');
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isModalAOpen, setISModalAOpen] = useState(true);

    const { user } = useAuth();

    const countries = [
        { code: 'US', name: 'United States' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' },
        { code: 'FR', name: 'France' },
        { code: 'DE', name: 'Germany' },
        { code: 'IT', name: 'Italy' },
        { code: 'ES', name: 'Spain' },
        { code: 'BR', name: 'Brazil' },
        { code: 'IN', name: 'India' },
        { code: 'JP', name: 'Japan' },
        { code: 'CN', name: 'China' },
        { code: 'MX', name: 'Mexico' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'NZ', name: 'New Zealand' },
        { code: 'IE', name: 'Ireland' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'BE', name: 'Belgium' },
        { code: 'SE', name: 'Sweden' },
        { code: 'NO', name: 'Norway' },
        { code: 'DK', name: 'Denmark' },
        { code: 'FI', name: 'Finland' },
        { code: 'AT', name: 'Austria' },
        { code: 'CH', name: 'Switzerland' },
        { code: 'SG', name: 'Singapore' },
        { code: 'KR', name: 'South Korea' },
        { code: 'AE', name: 'United Arab Emirates' },
        { code: 'AR', name: 'Argentina' },
        { code: 'CL', name: 'Chile' },
        { code: 'CO', name: 'Colombia' },
        { code: 'PL', name: 'Poland' },
        { code: 'CZ', name: 'Czech Republic' },
        { code: 'PT', name: 'Portugal' },
        { code: 'GR', name: 'Greece' },
        { code: 'TR', name: 'Turkey' },
        { code: 'TH', name: 'Thailand' },
        { code: 'MY', name: 'Malaysia' },
        { code: 'PH', name: 'Philippines' },
        { code: 'ID', name: 'Indonesia' },
        { code: 'SA', name: 'Saudi Arabia' },
        { code: 'EG', name: 'Egypt' },
        { code: 'NG', name: 'Nigeria' },
    ];

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            // STEP 1: Check if user already has a card FIRST
            console.log('Checking for existing cards for user:', user?.email);
            
            try {
                const existingCardsResponse = await axios.get('https://chainfree.info:7001/user/cards', {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                });
                
                if (existingCardsResponse.data.success && existingCardsResponse.data.data.length > 0) {
                    // User already has a card, skip everything
                    const existingCard = existingCardsResponse.data.data[0];
                    console.log('✅ User already has a card:', existingCard.card_id);
                    
                    setCardId(existingCard.card_id);
                    localStorage.setItem('cardId', existingCard.card_id);
                    
                    // Show success and navigate
                    setIsModalOpen(true);
                    setTimeout(() => {
                        setIsSuccess(true);
                    }, 2000);
                    
                    setTimeout(() => {
                        setIsModalOpen(false);
                    }, 4000);
                    
                    setTimeout(() => {
                        navigate('/autoPullpage');
                    }, 5000);
                    
                    return; // Exit early - no cardholder or card creation needed
                }
            } catch (checkError) {
                console.log('No existing cards found, proceeding with new card creation');
            }

            // STEP 2: No existing card, proceed with cardholder creation
            console.log('Creating new cardholder...');
            const response = await axios.post('https://chainfree.info:7000/create-cardholder', {
                firstName,
                lastName: surname,
                email,
                phoneNumber,
                address,
                city,
                state,
                country,
                postalCode,
            });

            if (response.data.success) {
                setTimeout(() => {
                    setIsSuccess(true);
                }, 4000);

                const newUserId = response.data.cardholder.id;
                console.log('Cardholder created:', response.data.cardholder);

                setStripeUserId(newUserId);

                // STEP 3: Create virtual card with new cardholder
                await createVirtualCard(newUserId);

                setIsModalOpen(true);

                setTimeout(() => {
                    setIsModalOpen(false);
                }, 6000);

                setTimeout(() => {
                    navigate('/autoPullpage');
                }, 7500);
                
            } else {
                console.error('Error creating cardholder:', response.data.error);
            }
        } catch (error) {
            console.error('Unexpected error:', error.response ? error.response.data : error.message);
        }
    };

    // Simplified createVirtualCard function (no need to check for existing cards here)
    const createVirtualCard = async (stripeUserId) => {
        try {
            console.log('Creating new virtual card with Stripe...');
            const response = await axios.post('https://chainfree.info:7000/create-virtual-card', {
                cardholderId: stripeUserId,
            });

            console.log('Full response:', response);

            if (response.data.success) {
                console.log('response in createVirtualCard:', response.data.cardDetails);

                const userCardId = response.data.cardDetails.id;
                setCardId(userCardId);
                localStorage.setItem('cardId', userCardId);
                console.log('Card ID stored in localStorage:', userCardId);
                
                // Link this card to the logged-in user in database
                try {
                    const linkResponse = await axios.post('https://chainfree.info:7001/user/link-card', {
                        card_id: userCardId,
                        card_name: 'My Virtual Card'
                    }, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                        }
                    });
                    
                    if (linkResponse.data.success) {
                        console.log('✅ Card created and linked to user successfully!');
                    } else {
                        console.error('❌ Failed to link card to user:', linkResponse.data.message);
                    }
                } catch (linkError) {
                    console.error('❌ Error linking card to user:', linkError);
                }
                
            } else {
                console.error('Error creating virtual card:', response.data.error);
            }
        } catch (error) {
            console.error('Unexpected error creating card:', error.response ? error.response.data : error.message);
        }
    };

    return (
        <>
            <GlobalStyle />
            <Container>
                <FormContainer>
                    

                    <FormCard onSubmit={handleSubmit}>
                        <FormHeader>
                            <FormTitle>Create Your Virtual Card</FormTitle>
                            <FormSubtitle>
                                Enter your details below to create a secure virtual payment card
                            </FormSubtitle>
                        </FormHeader>

                        <FormSplit>
                            {/* LEFT COLUMN - Personal Information */}
                            <FormColumn>
                                <SectionTitle>Personal Information</SectionTitle>
                                
                                <FormGroup>
                                    <Label htmlFor="firstName">First Name</Label>
                                    <Input
                                        id="firstName"
                                        placeholder="Enter your first name"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="surname">Last Name</Label>
                                    <Input
                                        id="surname"
                                        placeholder="Enter your last name"
                                        value={surname}
                                        onChange={(e) => setSurname(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="email">Email Address</Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Enter your email address"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="phone">Phone Number</Label>
                                    <PhoneInputWrapper>
                                        <PhoneInput
                                            placeholder="Enter phone number"
                                            value={phoneNumber}
                                            onChange={setPhoneNumber}
                                            defaultCountry="GB"
                                            international
                                            countryCallingCodeEditable={true}
                                        />
                                    </PhoneInputWrapper>
                                </FormGroup>
                            </FormColumn>

                            {/* RIGHT COLUMN - Address Information */}
                            <FormColumn>
                                <SectionTitle>Address Information</SectionTitle>
                                
                                <FormGroup>
                                    <Label htmlFor="address">Street Address</Label>
                                    <Input
                                        id="address"
                                        placeholder="Enter your street address"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="city">City</Label>
                                    <Input
                                        id="city"
                                        placeholder="Enter your city"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="state">State/Province</Label>
                                    <Input
                                        id="state"
                                        placeholder="Enter your state or province"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="country">Country</Label>
                                    <Select
                                        id="country"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        required
                                    >
                                        <option value="">Select your country</option>
                                        {countries.map((c) => (
                                            <option key={c.code} value={c.code}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </Select>
                                </FormGroup>
                                
                                <FormGroup>
                                    <Label htmlFor="postalCode">Postal Code</Label>
                                    <Input
                                        id="postalCode"
                                        placeholder="Enter your postal code"
                                        value={postalCode}
                                        onChange={(e) => setPostalCode(e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </FormColumn>
                        </FormSplit>

                        <ButtonGroup>
                            <Button 
                                type="button" 
                                className="secondary"
                                onClick={() => navigate('/autoPullpage')}
                            >
                                Skip for Now
                            </Button>
                            <Button 
                                type="submit" 
                                className="primary"
                            >
                                Create Virtual Card
                            </Button>
                        </ButtonGroup>
                    </FormCard>

                    <Footer>
                        Copyright © 2025 Hydrix LTD. Hydrix™ is a trademark of Hydrix LTD
                    </Footer>
                </FormContainer>

                {isModalOpen && (
                    <LoadingModal>
                        <LoadingContent>
                            <LoadingSpinner>
                                <ClipLoader color="#3B82F6" size={50} />
                            </LoadingSpinner>
                            <LoadingText>
                                {!isSuccess 
                                    ? "Creating your cardholder account..." 
                                    : "Account created successfully!"
                                }
                            </LoadingText>
                        </LoadingContent>
                    </LoadingModal>
                )}

                {isModalAOpen && (
                    <InfoModal onClose={() => setISModalAOpen(false)} />
                )}
            </Container>
        </>
    );
};

export default VirtualCard;